import { useUser } from '@gat-ship/user'
import type {
  GetSupporSessionsHistoryQueryParams,
  SupportSession
} from '@gat-ship/types'
import {
  isSupportSessionAssignedToSupportUser,
  removeSupportSessionById,
  shouldFetchSupportSessions
} from '../model/support-session-utils'
import { useSupportApiCalls } from './useSupportApiCalls'
import { useSupportSignalREvents } from '@gat-ship/signalr'
import { ref, shallowRef } from 'vue'

export type SupportSessionDataSource =
  | 'assigned'
  | 'my-support-sessions'
  | 'history'

type SupportSessionDataSourceOptions<T extends SupportSessionDataSource> =
  T extends 'history'
    ? {
        /** Whether to force a fetch of the data. @default false */
        forceFetch?: boolean
        /** Whether to subscribe to support session events. @default true */
        subscribeToEvents?: boolean
        /** The query parameters to fetch data from. */
        query?: GetSupporSessionsHistoryQueryParams
      }
    : {
        /** Whether to force a fetch of the data. @default false */
        forceFetch?: boolean
        /** Whether to subscribe to support session events. @default true */
        subscribeToEvents?: boolean
      }

/**
 * A composable that returns a data source for support sessions
 */
export function useSupportSessionDataSource<T extends SupportSessionDataSource>(
  dataSource: T
) {
  const user = useUser()
  const {
    getSupportSessionsHistoryRequest,
    getAssignedSupportSessionsRequest,
    getRegisteredSupportSessionsRequest
  } = useSupportApiCalls()

  const data = shallowRef<SupportSession[]>([])

  function setData(sessions: SupportSession[]) {
    data.value = sessions
  }

  function removeSupportSession(sessionId: string) {
    setData(removeSupportSessionById(data.value, sessionId))
  }

  /** Replayed by the SignalR handlers so a refetch keeps the last query. */
  const savedFetchOptions = ref<SupportSessionDataSourceOptions<T>>()

  const supportSignalREvents = useSupportSignalREvents({
    onSupportSessionRegistered: (event) => {
      const supportPersonUserId = event.message.supportPersonUserId
      if (
        isSupportSessionAssignedToSupportUser(
          supportPersonUserId,
          user.id.value
        )
      ) {
        fetchData(savedFetchOptions.value)
      }
    },
    onSupportSessionEnded: (event) => {
      if (user.isSupportUser.value) {
        removeSupportSession(event.message.sessionId)
        return
      }

      fetchData(savedFetchOptions.value)
    },
    onSupportSessionUpdated: () => {
      fetchData(savedFetchOptions.value)
    }
  })

  function subscribeToEvents() {
    supportSignalREvents.subscribeToSupportSessionEvents()
  }

  /** Fetch only on first load, unless the caller forces a refetch. */
  function shouldFetch(options?: SupportSessionDataSourceOptions<T>): boolean {
    return shouldFetchSupportSessions(
      data.value.length,
      Boolean(options?.forceFetch)
    )
  }

  async function fetchSessions(options?: SupportSessionDataSourceOptions<T>) {
    if (dataSource === 'history') {
      const historyOptions = options as {
        query?: GetSupporSessionsHistoryQueryParams
      }
      const result = await getSupportSessionsHistoryRequest(
        historyOptions?.query
      )
      setData(result.sessions)
    }
    if (dataSource === 'assigned') {
      const result = await getAssignedSupportSessionsRequest()
      setData(result.sessions)
    }
    if (dataSource === 'my-support-sessions') {
      const result = await getRegisteredSupportSessionsRequest()
      setData(result.sessions)
    }
  }

  async function fetchData(options?: SupportSessionDataSourceOptions<T>) {
    savedFetchOptions.value = options
    if (shouldFetch(options)) {
      await fetchSessions(options)
    }
    if (options?.subscribeToEvents ?? true) {
      subscribeToEvents()
    }
  }

  return {
    data,
    removeSupportSession,
    fetchData
  }
}
